import api from './authService';
import { openFilePreviewOrDownload } from '../utils/filePreview';
import { Project, TimesheetData, TimesheetSummary, SaveTimesheetEntry, TimesheetValidationData, LateUser } from '../types';

export const timesheetService = {
  // Get projects that the current user can log time for
  async getTimesheetProjects(
    userId?: number,
    options?: { includeClosed?: boolean }
  ): Promise<Project[]> {
    const params: Record<string, unknown> = {};
    if (userId) params.user_id = userId;
    if (options?.includeClosed) params.include_closed = true;
    const response = await api.get('/timesheet/projects', { params });
    return response.data.projects || [];
  },

  // Get timesheet entries for a specific week
  async getTimesheet(weekStart: string, userId?: number): Promise<TimesheetData> {
    const params: any = { week_start: weekStart };
    if (userId) params.user_id = userId;

    const response = await api.get('/timesheet', { params });
    return response.data;
  },

  // Save timesheet entries for a week (create/update/delete as needed)
  async saveTimesheet(weekStart: string, entries: SaveTimesheetEntry[], userId?: number): Promise<{ message: string }> {
    const payload: any = {
      week_start: weekStart,
      entries: entries
    };
    if (userId) payload.user_id = userId;

    const response = await api.post('/timesheet', payload);
    return response.data;
  },

  // Delete a specific timesheet entry
  async deleteTimesheetEntry(entryId: number): Promise<{ message: string }> {
    const response = await api.delete(`/timesheet/${entryId}`);
    return response.data;
  },

  // Get timesheet summary for a specific week
  async getTimesheetSummary(weekStart: string, userId?: number): Promise<TimesheetSummary> {
    const params: any = { week_start: weekStart };
    if (userId) params.user_id = userId;

    const response = await api.get('/timesheet/summary', { params });
    return response.data;
  },


  // Get detailed timesheet report data
  async getTimesheetReport(filters: any): Promise<any[]> {
    const response = await api.get('/timesheet/reporting', {
      params: filters
    });
    return response.data;
  },

  // Download timesheet report PDF
  async downloadTimesheetReportPDF(filters: any): Promise<void> {
    const response = await api.get('/timesheet/reporting/pdf', {
      params: filters,
      responseType: 'blob'
    });

    openFilePreviewOrDownload(
      new Blob([response.data]),
      `ND_TimeSheet_Report_${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.pdf`,
      'application/pdf'
    );
  },

  // Download timesheet report XLSX
  async downloadTimesheetReportXLSX(filters: any): Promise<void> {
    const response = await api.get('/timesheet/reporting/xlsx', {
      params: filters,
      responseType: 'blob'
    });

    openFilePreviewOrDownload(
      new Blob([response.data]),
      `ND_TimeSheet_Report_${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.xlsx`,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );
  },

  // Get clients for reporting filters
  async getClients(): Promise<any[]> {
    const response = await api.get('/timesheet/clients');
    return response.data.clients || [];
  },

  // Validation-related methods (PM/Admin only)
  async getTimesheetsForValidation(weekStart: string): Promise<TimesheetValidationData> {
    const response = await api.get('/timesheet/validation', {
      params: { week_start: weekStart }
    });
    return response.data;
  },

  async validateTimesheetEntry(entryId: number): Promise<{ message: string }> {
    const response = await api.post(`/timesheet/validation/${entryId}/validate`);
    return response.data;
  },

  async rejectTimesheetEntry(entryId: number): Promise<{ message: string }> {
    const response = await api.post(`/timesheet/validation/${entryId}/reject`);
    return response.data;
  },

  async getLateUsers(weekStart: string): Promise<{ late_users: LateUser[] }> {
    const response = await api.get('/timesheet/validation/late-users', {
      params: { week_start: weekStart }
    });
    return response.data;
  },

  async getLateUsersConsolidated(): Promise<{ late_users: LateUser[] }> {
    const response = await api.get('/timesheet/validation/late-users-consolidated');
    return response.data;
  },

  async sendReminders(reminders: Array<{ user_id: number; delayed_weeks: string[] }>): Promise<{ message: string; sent_count: number }> {
    const response = await api.post('/timesheet/validation/send-reminder', {
      reminders: reminders
    });
    return response.data;
  },

  async submitTimesheetForValidation(weekStart: string, userId?: number, comment?: string): Promise<{ message: string }> {
    const payload: any = {
      week_start: weekStart
    };
    if (userId) payload.user_id = userId;
    if (comment) payload.comment = comment;

    const response = await api.post('/timesheet/submit-for-validation', payload);
    return response.data;
  },

  async updateTimesheetEntry(entryId: number, hours: number): Promise<{ message: string; old_hours: number; new_hours: number }> {
    const response = await api.post('/timesheet/validation/update-entry', {
      entry_id: entryId,
      hours: hours
    });
    return response.data;
  },

  async upsertSubmittedTimesheetEntry(payload: {
    entry_id?: number;
    hours: number;
    user_id?: number;
    date?: string;
    project_id?: number;
    work_package_id?: number | null;
    work_unit_id?: number | null;
  }): Promise<{ message: string; old_hours: number; new_hours: number; created?: boolean; entry_id?: number }> {
    const response = await api.post('/timesheet/validation/update-entry', payload);
    return response.data;
  },

  // Saved Report Ranges
  async getSavedRanges(): Promise<any[]> {
    const response = await api.get('/timesheet/reporting/ranges');
    return response.data;
  },

  async saveReportRange(name: string, startDate: string, endDate: string): Promise<any> {
    const response = await api.post('/timesheet/reporting/ranges', {
      name,
      start_date: startDate,
      end_date: endDate
    });
    return response.data;
  },

  async deleteSavedRange(rangeId: number): Promise<{ message: string }> {
    const response = await api.delete(`/timesheet/reporting/ranges/${rangeId}`);
    return response.data;
  },
};

export default timesheetService;